import type { AppearanceLook } from './appearance.util';

/** A one-colour look with a name, offered as a swatch. */
export interface AppearancePreset {
  id: string;
  name: string;
  hue: number;
  tone: number;
  intensity: number;
}

/** Swatches shown at once; the arrows page through the rest. */
export const PRESET_PAGE_SIZE = 9;

/**
 * Eighteen looks, nine a page; the first page is Arc's row. Each gives a light
 * and a dark tint, and every one keeps the chrome readable in both modes at
 * any grain (appearance-colour.util.test.ts).
 */
export const APPEARANCE_PRESETS: readonly AppearancePreset[] = [
  { id: 'cream', name: 'Cream', hue: 80, tone: 85, intensity: 35 },
  { id: 'pink', name: 'Pink', hue: 350, tone: 45, intensity: 70 },
  { id: 'purple', name: 'Purple', hue: 305, tone: 40, intensity: 65 },
  { id: 'red', name: 'Red', hue: 22, tone: 40, intensity: 65 },
  { id: 'orange', name: 'Orange', hue: 50, tone: 45, intensity: 75 },
  { id: 'yellow', name: 'Yellow', hue: 95, tone: 55, intensity: 80 },
  { id: 'green', name: 'Green', hue: 150, tone: 40, intensity: 55 },
  { id: 'blue', name: 'Blue', hue: 245, tone: 40, intensity: 65 },
  { id: 'slate', name: 'Slate', hue: 250, tone: 35, intensity: 15 },
  { id: 'rose', name: 'Rose', hue: 5, tone: 60, intensity: 45 },
  { id: 'lavender', name: 'Lavender', hue: 285, tone: 55, intensity: 50 },
  { id: 'mint', name: 'Mint', hue: 165, tone: 55, intensity: 50 },
  { id: 'sky', name: 'Sky', hue: 225, tone: 50, intensity: 55 },
  { id: 'teal', name: 'Teal', hue: 195, tone: 40, intensity: 60 },
  { id: 'sand', name: 'Sand', hue: 70, tone: 50, intensity: 30 },
  { id: 'olive', name: 'Olive', hue: 115, tone: 35, intensity: 45 },
  { id: 'indigo', name: 'Indigo', hue: 270, tone: 30, intensity: 70 },
  { id: 'graphite', name: 'Graphite', hue: 0, tone: 30, intensity: 0 },
];

export const PRESET_PAGES = Math.ceil(
  APPEARANCE_PRESETS.length / PRESET_PAGE_SIZE
);

export function presetPage(page: number): readonly AppearancePreset[] {
  return APPEARANCE_PRESETS.slice(
    page * PRESET_PAGE_SIZE,
    (page + 1) * PRESET_PAGE_SIZE
  );
}

/**
 * The preset a look is, or null: one colour whose hue, tone and intensity
 * match. It is worked out, never saved, so moving the handle leaves it.
 */
export function presetFor(look: AppearanceLook): AppearancePreset | null {
  if (look.colors.length !== 1) return null;
  const [color] = look.colors;
  return (
    APPEARANCE_PRESETS.find(
      (preset) =>
        preset.hue === color.hue &&
        preset.tone === color.tone &&
        preset.intensity === look.intensity
    ) ?? null
  );
}

/** The look a preset gives, keeping the grain. */
export function applyPreset(
  preset: AppearancePreset,
  look: AppearanceLook
): AppearanceLook {
  return {
    colors: [{ hue: preset.hue, tone: preset.tone }],
    intensity: preset.intensity,
    grain: look.grain,
  };
}
